import { useApolloClient } from "@apollo/client";
import yaml from "js-yaml";
import { get, groupBy } from "lodash";
import { useCallback } from "react";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { createScreenDraft } from "utils/helpers/screenDraft";
import { configToProcess, parseConfig } from "./importConfig";
import { DEFAULT_FIELDS_STRATEGY } from "./useProcessFieldsImport";

export const IMPORT_KINDS = {
  CANVAS: "canvas",
  FIELDS: "fields",
};

export const IMPORT_ERROR_CODES = {
  FIELDS_REQUIRE_EXISTING_PROCESS: "FIELDS_REQUIRE_EXISTING_PROCESS",
  INVALID_FILE: "INVALID_FILE",
};

const toMappingInput = (mapping) => ({
  externalId: mapping.externalId,
  status: { id: mapping.statusId },
  name: mapping.name,
  binding: mapping.binding,
});

const byProjectMappings = (screenMappings) =>
  groupBy(
    screenMappings.filter((mapping) => mapping.uiProject),
    "uiProject",
  );

const hasStatuses = (config) => {
  const statuses = config?.statuses || config?.processStatuses;
  return Array.isArray(statuses) && statuses.length > 0;
};

const hasFields = (config) => Array.isArray(config?.fields);

const findExistingStatus = (existingProcess, screen) => {
  const statuses = get(existingProcess, "processStatuses") || [];
  return (
    statuses.find((status) => status.id === screen.statusId) ||
    statuses.find(
      (status) =>
        status.name &&
        screen.name &&
        status.name.toLowerCase() === String(screen.name).toLowerCase(),
    )
  );
};

/** Rebuild a fields-import YAML from a canvas (or fields-only) config object. */
export const fieldsContentFromConfig = (config) => {
  if (!hasFields(config)) return null;
  return yaml.dump(
    {
      process: config.process || config.name || "process",
      referentiels: config.referentiels || {},
      fields: config.fields,
      mappings: config.mappings || [],
    },
    { noRefs: true, sortKeys: false, lineWidth: 120 },
  );
};

export const useProcessImport = () => {
  const client = useApolloClient();
  const [addProcess] = useMutation(graphql.mutations.CREATE_PROCESS);
  const [updateProcess] = useMutation(graphql.mutations.UPDATE_PROCESS);
  const [importScreens] = useMutation(
    graphql.mutations.IMPORT_UI_PROVIDER_SCREENS,
  );
  const [addScreen] = useMutation(graphql.mutations.ADD_SCREEN);
  const [addScreenVersion] = useMutation(graphql.mutations.ADD_SCREEN_VERSION);
  const [updateScreen] = useMutation(graphql.mutations.UPDATE_SCREEN);
  const [validateFieldsImport] = useMutation(
    graphql.mutations.VALIDATE_PROCESS_FIELDS_IMPORT,
  );
  const [executeFieldsImport] = useMutation(
    graphql.mutations.EXECUTE_PROCESS_FIELDS_IMPORT,
  );

  const validateFields = useCallback(
    async (content, processId) => {
      const { data } = await validateFieldsImport({
        variables: {
          data: {
            process: { id: processId },
            content,
            strategy: DEFAULT_FIELDS_STRATEGY,
          },
        },
      });
      return data?.validateProcessFieldsImport || null;
    },
    [validateFieldsImport],
  );

  const applyFields = useCallback(
    async (content, processId) => {
      const { data } = await executeFieldsImport({
        variables: {
          data: {
            process: { id: processId },
            content,
            strategy: DEFAULT_FIELDS_STRATEGY,
          },
        },
      });
      return data?.executeProcessFieldsImport || null;
    },
    [executeFieldsImport],
  );

  const prepareImport = useCallback(
    async (file, { existingProcess } = {}) => {
      const text = await file.text();
      let config;
      try {
        config = parseConfig(text, file.name);
      } catch (_error) {
        throw new Error(IMPORT_ERROR_CODES.INVALID_FILE);
      }

      if (!config || typeof config !== "object") {
        throw new Error(IMPORT_ERROR_CODES.INVALID_FILE);
      }

      // Fields-only YAML: import FieldDefinitions onto the open process.
      if (hasFields(config) && !hasStatuses(config)) {
        if (!existingProcess?.id) {
          throw new Error(IMPORT_ERROR_CODES.FIELDS_REQUIRE_EXISTING_PROCESS);
        }
        const fieldsContent = text;
        const fieldsPreview = await validateFields(
          fieldsContent,
          existingProcess.id,
        );
        return {
          kind: IMPORT_KINDS.FIELDS,
          config,
          existingProcess,
          processInput: {
            id: existingProcess.id,
            name: existingProcess.name,
            type: existingProcess.type,
          },
          screenMappings: [],
          fieldsContent,
          fieldsPreview,
        };
      }

      const { processInput, screenMappings, embeddedScreens } = configToProcess(
        config,
        { existingProcess },
      );

      let fieldsContent = null;
      let fieldsPreview = null;
      if (hasFields(config)) {
        fieldsContent = fieldsContentFromConfig(config);
        fieldsPreview = await validateFields(fieldsContent, processInput.id);
      }

      return {
        kind: IMPORT_KINDS.CANVAS,
        config,
        processInput,
        screenMappings,
        embeddedScreens,
        existingProcess,
        fieldsContent,
        fieldsPreview,
      };
    },
    [validateFields],
  );

  const preflight = useCallback(
    async ({ screenMappings }) => {
      if (!screenMappings || !screenMappings.length) return [];
      const results = [];
      for (const [uiProject, mappings] of Object.entries(
        byProjectMappings(screenMappings),
      )) {
        const { data } = await client.query({
          query: graphql.queries.PREFLIGHT_UI_PROVIDER_SCREENS,
          variables: {
            provider: { project: { id: uiProject } },
            mappings: mappings.map(toMappingInput),
          },
          fetchPolicy: "network-only",
        });
        for (const item of data?.preflightUiProviderScreens || [])
          results.push({ ...item, uiProject });
      }
      return results;
    },
    [client],
  );

  const writeEmbeddedScreen = useCallback(
    async (screen, { statusId, screenId, versionNumber }) =>
      createScreenDraft({
        addScreen,
        addScreenVersion,
        updateScreen,
        published: true,
        statusId,
        screenId,
        description: screen.name || "Imported screen",
        versionNumber,
        htmlContent: screen.htmlContent,
        styleContent: screen.styleContent || "",
        slots: screen.slots || [],
        states: screen.states || [],
        copyStates: true,
        translations: screen.translations || {},
      }),
    [addScreen, addScreenVersion, updateScreen],
  );

  const applyEmbeddedScreens = useCallback(
    async (embeddedScreens, existingProcess) => {
      const results = [];
      for (const screen of embeddedScreens || []) {
        const existingStatus = findExistingStatus(existingProcess, screen);
        const existingScreen = get(existingStatus, "screens[0]");
        const statusId = screen.statusId || get(existingStatus, "id");
        try {
          let created;
          try {
            created = await writeEmbeddedScreen(screen, {
              statusId,
              screenId: get(existingScreen, "id"),
              versionNumber: get(
                existingScreen,
                "currentPublishedVersion.versionNumber",
              ),
            });
          } catch (updateError) {
            if (!get(existingScreen, "id")) throw updateError;
            created = await writeEmbeddedScreen(screen, { statusId });
          }
          results.push({
            status: { id: statusId },
            screen: { id: created.screenId },
          });
        } catch (error) {
          results.push({
            status: { id: statusId },
            error: get(error, "message") || String(error),
          });
        }
      }
      return results;
    },
    [writeEmbeddedScreen],
  );

  const runImport = useCallback(
    async ({
      kind,
      processInput,
      screenMappings,
      embeddedScreens,
      existingProcess,
      fieldsContent,
    }) => {
      let id = processInput?.id;
      let results = [];

      if (kind === IMPORT_KINDS.CANVAS) {
        if (existingProcess)
          await updateProcess({
            variables: { where: { id: processInput.id }, data: processInput },
          });
        else await addProcess({ variables: { data: processInput } });

        id = processInput.id;

        const embeddedResults = await applyEmbeddedScreens(
          embeddedScreens,
          existingProcess,
        );
        results.push(...embeddedResults);

        for (const [uiProject, mappings] of Object.entries(
          byProjectMappings(screenMappings),
        )) {
          const { data } = await importScreens({
            variables: {
              provider: { project: { id: uiProject } },
              mappings: mappings.map(toMappingInput),
            },
          });
          for (const item of data?.importUiProviderScreens || [])
            results.push(item);
        }
      }

      let fieldsResult = null;
      if (fieldsContent && id) {
        fieldsResult = await applyFields(fieldsContent, id);
        if (fieldsResult && !fieldsResult.success) {
          const detail = (fieldsResult.errors || [])
            .map((entry) => entry.key)
            .join(", ");
          throw new Error(detail || "FIELDS_IMPORT_FAILED");
        }
      }

      return { id, results, fieldsResult };
    },
    [
      addProcess,
      updateProcess,
      importScreens,
      applyFields,
      applyEmbeddedScreens,
    ],
  );

  return { prepareImport, preflight, runImport };
};
